import { ExternalLink, Lightbulb, ShieldAlert, CheckCircle2 } from "lucide-react";

function Block({ block }) {
  if (block.type === "p") {
    return <p className="text-base text-zinc-700 leading-relaxed mb-3">{block.text}</p>;
  }
  if (block.type === "h3") {
    return <h3 className="font-heading font-700 text-lg md:text-lg mt-6 mb-2 text-zinc-900">{block.text}</h3>;
  }
  if (block.type === "list") {
    return (
      <ul className="list-disc pl-5 space-y-1.5 text-base text-zinc-700 mb-4">
        {block.items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    );
  }
  if (block.type === "checks") {
    return (
      <ul className="space-y-2 mb-4">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-base text-zinc-800">
            <CheckCircle2 className="w-4 h-4 mt-1 text-emerald-600 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "callout") {
    const isWarn = block.tone === "warn";
    const Icon = isWarn ? ShieldAlert : Lightbulb;
    const cls = isWarn
      ? "border-amber-300 bg-amber-50 text-amber-900"
      : "border-sky-200 bg-sky-50 text-sky-900";
    return (
      <div className={`border ${cls} p-4 flex gap-3 text-sm mb-4`}>
        <Icon className="w-5 h-5 shrink-0 mt-0.5" />
        <p>{block.text}</p>
      </div>
    );
  }
  return null;
}

export default function VisaFeaturedArticle({ article }) {
  if (!article) return null;
  return (
    <article
      className="border border-zinc-300 bg-white p-6 sm:p-10"
      data-testid={`visa-featured-article-${article.id}`}
    >
      <div className="text-xs font-mono uppercase tracking-widest text-[#0e7490] mb-2">
        Editorial · Traveler Guide
      </div>
      <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight mb-3">
        {article.title}
      </h2>
      {article.dek && (
        <p className="text-lg text-zinc-600 leading-relaxed mb-6 border-l-4 border-[#0e7490] pl-4">
          {article.dek}
        </p>
      )}

      {article.image && (
        <figure className="mb-6" data-testid={`visa-featured-image-${article.id}`}>
          <img
            src={article.image.src}
            alt={article.image.alt}
            loading="lazy"
            className="w-full h-auto border border-zinc-200"
          />
          <figcaption className="text-xs text-muted-foreground mt-2 italic">
            {article.image.alt}
          </figcaption>
        </figure>
      )}

      <div>
        {article.body.map((b, i) => <Block key={i} block={b} />)}
      </div>

      {article.official_link && (
        <div className="mt-6 border border-zinc-200 bg-zinc-50 p-4" data-testid={`visa-featured-link-${article.id}`}>
          <div className="text-xs font-mono uppercase tracking-wide text-zinc-500 mb-1">Official website</div>
          <a
            href={article.official_link.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-primary font-700 text-sm hover:underline"
          >
            {article.official_link.label} <ExternalLink className="w-3.5 h-3.5" />
          </a>
          {article.official_link.note && (
            <p className="text-xs text-muted-foreground mt-1">{article.official_link.note}</p>
          )}
        </div>
      )}

      {article.hashtags?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-1.5">
          {article.hashtags.map((h) => (
            <span key={h} className="text-xs font-mono text-[#0e7490] bg-cyan-50 border border-cyan-200 px-2 py-0.5">
              #{h}
            </span>
          ))}
        </div>
      )}

      {article.published_at && (
        <p className="text-xs text-muted-foreground mt-6 border-t border-zinc-100 pt-3">
          Published {new Date(article.published_at).toLocaleDateString()} — Always verify visa fees and eligibility on the official portal before applying.
        </p>
      )}
    </article>
  );
}
